/**
 * Cash Drawer Utility Functions for Epson Thermal Printers
 * ESC/POS Commands for controlling cash drawer
 */

/**
 * Opens the cash drawer using ESC/POS command
 * ESC p m t1 t2 = 0x1B 0x70 m t1 t2
 * m: Pin number (0 = pin 2, 1 = pin 5)
 * t1, t2: Pulse duration (t1 * 2ms ON time, t2 * 2ms OFF time)
 * 
 * @param {number} pin - Pin number (0 or 1, default 0)
 * @returns {Promise<boolean>} Success status
 */
export const openCashDrawer = async (pin = 0) => {
  try {
    
    // ESC/POS command to open cash drawer
    // \x1B\x70\x00\x14\x14 for pin 2 (most common)
    // \x1B\x70\x01\x14\x14 for pin 5
    const escPosCommand = `\x1B\x70${String.fromCharCode(pin)}\x14\x14`;
    
    // Create a minimal print window with auto-print
    const printWindow = window.open('', '_blank', 'width=1,height=1,left=10000,top=10000');
    
    if (!printWindow) {

      return openCashDrawerFallback(pin);
    }
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Cash Drawer</title>
          <style>
            body { 
              margin: 0; 
              padding: 0;
              width: 1px;
              height: 1px;
            }
            pre {
              font-size: 1px;
              margin: 0;
              padding: 0;
            }
          </style>
        </head>
        <body>
          <pre>${escPosCommand}</pre>
          <script>
            // Auto-print and close
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 500);
              }, 100);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    return true;
  } catch (error) {

    return false;
  }
};

/**
 * Fallback method using iframe (may show print dialog)
 * @param {number} pin - Pin number (0 or 1)
 * @returns {Promise<boolean>} Success status
 */
const openCashDrawerFallback = async (pin = 0) => {
  try {
    const escPosCommand = `\x1B\x70${String.fromCharCode(pin)}\x14\x14`;
    
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:absolute;width:1px;height:1px;left:-1000px;top:-1000px;';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <html>
        <head>
          <style>body{margin:0;padding:0;width:1px;height:1px;}pre{font-size:1px;}</style>
        </head>
        <body><pre>${escPosCommand}</pre></body>
      </html>
    `);
    doc.close();
    
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 1000);
    }, 100);
    
    return true;
  } catch (error) {

    return false;
  }
};

/**
 * Opens cash drawer with HTML that includes the command
 * This method embeds the ESC/POS command in the receipt HTML
 * 
 * @param {string} receiptHtml - The receipt HTML to print
 * @param {number} pin - Pin number (0 or 1, default 0)
 * @returns {string} Modified HTML with drawer open command
 */
export const addDrawerCommandToReceipt = (receiptHtml, pin = 0) => {
  const escPosCommand = `\x1B\x70${String.fromCharCode(pin)}\x14\x14`;
  
  // Insert the command at the start of the body
  const modifiedHtml = receiptHtml.replace(
    '<body>',
    `<body><pre style="display:none;">${escPosCommand}</pre>`
  );
  
  return modifiedHtml;
};

/**
 * Check if cash drawer should be opened based on settings
 * 
 * @param {boolean} openDrawerOnPrint - User setting
 * @param {string} paymentMethod - Payment method used
 * @returns {boolean} Whether to open drawer
 */
export const shouldOpenDrawer = (openDrawerOnPrint, paymentMethod) => {
  // Only open for cash payments, or if explicitly enabled for all
  const isCashPayment = paymentMethod?.toUpperCase?.() === 'CASH';
  
  // Open if toggle is on AND it's a cash payment
  // Or if toggle is on and user wants it for all payments
  return openDrawerOnPrint;
};

/**
 * Alternative method using Web Serial API (for modern browsers)
 * Requires user permission but works WITHOUT print dialog
 * 
 * @param {number} pin - Pin number (0 or 1, default 0)
 * @returns {Promise<boolean>} Success status
 */
export const openCashDrawerSerial = async (pin = 0) => {
  try {
    if (!('serial' in navigator)) {

      return openCashDrawer(pin); // Fallback to print method
    }
    
    // Check if we have a stored port
    let port = null;
    const ports = await navigator.serial.getPorts();
    
    if (ports.length > 0) {
      port = ports[0]; // Use first available port
    } else {
      // Request port access (one-time permission)
      port = await navigator.serial.requestPort();
    }
    
    // Open connection
    await port.open({ baudRate: 9600 });
    
    // ESC/POS command
    const command = new Uint8Array([0x1B, 0x70, pin, 0x14, 0x14]);
    
    const writer = port.writable.getWriter();
    await writer.write(command);
    writer.releaseLock();
    
    await port.close();
    
    return true;
  } catch (error) {
    if (error.name === 'NotFoundError') {

    } else {

    }
    return false;
  }
};

export default {
  openCashDrawer,
  addDrawerCommandToReceipt,
  shouldOpenDrawer,
  openCashDrawerSerial,
};
